import { useEffect, type Dispatch, type SetStateAction } from "react";
import { FindRideHero } from "../components/FindRideHero";
import { FindRideOutcomeModal } from "../components/FindRideOutcomeModal";
import { FindRidePageShell } from "../components/FindRidePageShell";
import { FindRideResultsList } from "../components/FindRideResultsList";
import { FindRideSearchForm } from "../components/FindRideSearchForm";
import { FindRideTrustStrip } from "../components/FindRideTrustStrip";
import { useFindRideSearch } from "../hooks/useFindRideSearch";
import { tFindRide } from "../locales/FindRideTranslations";
import type { AuthMode } from "../types/findRide.types";

export type FindRidePageProps = {
  showAuthModal?: boolean;
  setShowAuthModal?: Dispatch<SetStateAction<boolean>>;
  setAuthMode?: Dispatch<SetStateAction<AuthMode>>;
};

/**
 * Form → button loader → outcome modal → (View & Book) results cards only.
 */
export default function FindRidePage({
  showAuthModal = false,
  setShowAuthModal,
  setAuthMode,
}: FindRidePageProps = {}) {
  const {
    criteria,
    formPrefill,
    rides,
    totalRides,
    isSearching,
    outcome,
    submitError,
    notifySaved,
    isSavingNotify,
    notifyError,
    submitSearch,
    clearOutcome,
    refreshSearch,
    viewAndBook,
    closeOutcomeModal,
    editDetails,
    saveNotifyLater,
  } = useFindRideSearch({ setShowAuthModal, setAuthMode });

  useEffect(() => {
    const prevTitle = document.title;
    document.title = tFindRide("pageTitle");

    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content") ?? "";
    if (meta) {
      meta.setAttribute("content", tFindRide("pageMetaDescription"));
    }

    return () => {
      document.title = prevTitle;
      if (meta) meta.setAttribute("content", prevDesc);
    };
  }, []);

  const showResultsOnly = outcome.kind === "ride-list" && criteria;

  return (
    <FindRidePageShell>
      {showResultsOnly ? (
        <FindRideResultsList
          criteria={criteria}
          rides={rides}
          totalRides={totalRides}
          onEditDetails={editDetails}
        />
      ) : (
        <>
          <FindRideHero />
          <FindRideSearchForm
            initialCriteria={criteria ?? formPrefill}
            isSearching={isSearching}
            submitError={submitError}
            onSubmitSearch={submitSearch}
            onCriteriaChange={clearOutcome}
          />
          <FindRideTrustStrip />
        </>
      )}

      <FindRideOutcomeModal
        outcome={outcome}
        authOverlayOpen={showAuthModal}
        isRefreshing={isSearching}
        notifySaved={notifySaved}
        isSavingNotify={isSavingNotify}
        notifyError={notifyError}
        onOpenChange={closeOutcomeModal}
        onRefresh={refreshSearch}
        onSaveNotify={() => {
          void saveNotifyLater();
        }}
        onViewAndBook={viewAndBook}
      />
    </FindRidePageShell>
  );
}
